import { useEffect } from "react";
import useCanvasStore from "../stores/canvasStore";

// Hook that provides a clean API and handles PIXI initialization
export const useCanvas = (containerRef = null) => {
  const store = useCanvasStore();

  // Initialize PIXI when containerRef is provided
  useEffect(() => {
    if (containerRef?.current && !store.pixiInitialized) {
      console.log("Initializing PIXI from useCanvas...");
      store.initializePixi(containerRef);
    }
  }, [containerRef]);

  // Handle container resize
  useEffect(() => {
    if (!store.pixiApp || !store.viewport || !containerRef?.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        // Resize PIXI app to match container
        store.pixiApp.renderer.resize(width, height);
        store.viewport.resize(width, height);

        setTimeout(() => store.centerAndFitViewport(), 0);

        console.log("Container resized to:", width, "x", height);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [store.pixiApp, store.viewport, containerRef]);

  // Cleanup ONLY when the owner component unmounts
  useEffect(() => {
    if (!containerRef) return;

    return () => {
      if (store.pixiInitialized) {
        console.log("Owner component unmounting - cleaning up PIXI...");
        store.destroyPixi();
      }
    };
  }, []);

  // Provide backward compatibility for selectedElement
  const selectedElement =
    store.selectedElements.length === 1 ? store.selectedElements[0] : null;

  // Return the same clean API with new timeline methods
  return {
    // State
    projectName: store.projectName,
    setProjectName: store.setProjectName,
    selectedElement, // Backward compatibility - single element from selectedElements
    selectedElements: store.selectedElements, // Array for both single and multi-selection
    canvasElements: store.canvasElements,
    canvasSize: store.canvasSize,
    canvasBackgroundColor: store.canvasBackgroundColor,
    currentCanvasZoom: store.canvasZoom,
    isPlaying: store.isPlaying,
    currentTime: store.currentTime,
    duration: store.duration,

    // PIXI State
    pixiApp: store.pixiApp,
    viewport: store.viewport,
    visualContainer: store.visualContainer,
    pixiInitialized: store.pixiInitialized,
    videoTextureManager: store.videoTextureManager,

    // Basic Actions
    selectElement: store.selectElement,
    selectElements: store.selectElements,
    addToSelection: store.addToSelection,
    toggleElementSelection: store.toggleElementSelection,
    selectElementsInRange: store.selectElementsInRange,
    clearSelection: store.clearSelection,
    isElementSelected: store.isElementSelected,
    getSelectedElement: store.getSelectedElement, // Helper for single selection
    hasMultipleSelection: store.hasMultipleSelection, // Helper to check multi-selection
    addElement: store.addElement,
    updateElement: store.updateElement,
    deleteElement: store.deleteElement,
    reorderElement: store.reorderElement,
    moveSelectedElements: store.moveSelectedElements,
    setCanvasSize: store.setCanvasSize,
    setCanvasBackgroundColor: store.setCanvasBackgroundColor,
    zoomIn: store.zoomIn,
    zoomOut: store.zoomOut,
    setTimelineZoom: store.setTimelineZoom,
    requestAddItem: store.requestAddItem,
    clearAddItemRequest: store.clearAddItemRequest,
    addItemRequest: store.addItemRequest,

    // PIXI Actions
    initializePixi: store.initializePixi,
    destroyPixi: store.destroyPixi,
    resizePixi: store.resizePixi,
    centerAndFitViewport: store.centerAndFitViewport,

    // Layer management
    sendForward: store.sendForward,
    sendBackward: store.sendBackward,
    bringToFront: store.bringToFront,
    sendToBack: store.sendToBack,

    // Timeline Methods
    updateElementTimeline: store.updateElementTimeline,
    updateMultipleElementTimelines: store.updateMultipleElementTimelines, // NEW
    getElementsAtTime: store.getElementsAtTime,
    getTimelineTracks: store.getTimelineTracks,
    setCurrentTime: store.setCurrentTime,
    setIsPlaying: store.setIsPlaying,
    togglePlayback: store.togglePlayback,
    stopPlaybackLoop: store.stopPlaybackLoop,
    toggleMute: store.toggleMute,
    updateSpritesForTime: store.updateSpritesForTime, // Add this for thumbnail capture
    setIsElementReloadingFn: store.setIsElementReloadingFn, // Set function to check if element is reloading

    // Undo/Redo
    undo: store.undo,
    redo: store.redo,
    canUndo: store.canUndo,
    canRedo: store.canRedo,
    clearHistory: store.clearHistory,
    getHistoryInfo: store.getHistoryInfo,

    // Utility methods
    getSortedElements: store.getSortedElements,
    getElementLayerPosition: store.getElementLayerPosition,
    canMoveForward: store.canMoveForward,
    canMoveBackward: store.canMoveBackward,

    // Copy/Paste
    copyElements: store.copyElements,
    getClipboard: store.getClipboard,
    pasteElements: store.pasteElements,
  };
};
